<template>
  <q-form @submit="this.onSubmit" class="ms-form form">
    <div v-if="this.isFetching" class="form__loading">
      <q-spinner color="primary" size="32px" />
    </div>

    <template v-else>
      <div class="form__layout">
        <div class="form__list">
          <q-input
            v-model="this.search"
            dense outlined clearable
            placeholder="Поиск шага..."
            class="form__search"
          >
            <template #prepend>
              <q-icon name="search" size="18px" />
            </template>
          </q-input>

          <div v-if="!filteredSteps.length" class="form__empty">
            Нет доступных шагов для добавления.
          </div>

          <div v-else class="form__list-scroll">
            <button
              v-for="step in filteredSteps"
              :key="step.step_id"
              type="button"
              class="form__step"
              :class="{ 'form__step--active': step.step_id === selectedStepId }"
              @click="selectStep(step.step_id)"
            >
              <span class="form__step-id">{{ step.step_id }}</span>
              <span class="form__step-name">{{ step.step_name }}</span>
              <span class="form__step-badges">
                <span class="form__badge" title="Условия">{{ counts(step.step_id).conditions }}</span>
                <span class="form__badge" title="Действия">{{ counts(step.step_id).actions }}</span>
                <span class="form__badge" title="Условия вхождения">{{ counts(step.step_id).include }}</span>
              </span>
            </button>
          </div>
        </div>

        <div class="form__preview">
          <div v-if="!selectedStep" class="form__preview-empty">
            Выберите шаг слева, чтобы посмотреть его содержимое.
          </div>

          <template v-else>
            <div class="form__preview-title">
              <span class="form__step-id">{{ selectedStep.step_id }}</span>
              {{ selectedStep.step_name }}
            </div>
            <div v-if="selectedStep.step_code" class="form__preview-code">
              {{ selectedStep.step_code }}
            </div>

            <preview-block
              title="Условия"
              icon="rule"
              :items="previewConditions"
              empty-text="Нет условий"
              label-key="condition_type_name"
              sub-key="comment"
            />
            <preview-block
              title="Действия"
              icon="bolt"
              :items="previewActivities"
              empty-text="Нет действий"
              label-key="activity_name"
              sub-key="comment"
            />
            <preview-block
              title="Условия вхождения"
              icon="star"
              :items="previewInclude"
              empty-text="Нет условий вхождения"
              label-key="condition_type_name"
              sub-key="comment"
            />
          </template>
        </div>
      </div>

      <div class="form__active">
        <CustomCheckbox
          color="primary"
          label="Активный"
          :value="!!this.formData.is_active"
          @change="(value) => onChange('is_active', value)"
        />
      </div>

      <CustomModalFooter
        :btn-loading="this.loading"
        submit-btn-text="Выбрать"
        :disable-submit="!selectedStepId"
      />
    </template>
  </q-form>
</template>

<script>
import CustomModalFooter from "@/components/modal/modals/modal-footer";
import CustomCheckbox from "@/components/form/CustomCheckbox";
import PreviewBlock from "@/components/modal/modals/add-grade-timeline-step/preview-block";
import { loadStepLibraryPreviewData } from "@/components/modal/modals/add-grade-timeline-step/services";

const PREVIEW_LIMIT = 4;

export default {
  name: "add-grade-timeline-step",
  components: { CustomCheckbox, CustomModalFooter, PreviewBlock },
  props: ["data"],
  data() {
    return {
      loading: false,
      isFetching: true,
      search: "",
      steps: [],
      conditionsByStep: {},
      activitiesByStep: {},
      includeByStep: {},
      selectedStepId: this.data?.body?.step_id || null,
      formData: {
        step_id: this.data?.body?.step_id || "",
        is_active: this.data?.body?.is_active !== false,
      },
    };
  },
  computed: {
    filteredSteps() {
      const q = this.search.trim().toLowerCase();
      if (!q) return this.steps;
      return this.steps.filter(
        (step) =>
          String(step.step_id).includes(q) ||
          (step.step_name || "").toLowerCase().includes(q) ||
          (step.step_code || "").toLowerCase().includes(q)
      );
    },
    selectedStep() {
      return this.steps.find((step) => step.step_id === this.selectedStepId) || null;
    },
    previewConditions() {
      return this.slicePreview(this.conditionsByStep[this.selectedStepId]);
    },
    previewActivities() {
      return this.slicePreview(this.activitiesByStep[this.selectedStepId]);
    },
    previewInclude() {
      return this.slicePreview(this.includeByStep[this.selectedStepId]);
    },
  },
  methods: {
    slicePreview(items) {
      const list = items || [];
      return {
        rows: list.slice(0, PREVIEW_LIMIT),
        total: list.length,
        rest: Math.max(0, list.length - PREVIEW_LIMIT),
      };
    },
    counts(stepId) {
      return {
        conditions: (this.conditionsByStep[stepId] || []).length,
        actions: (this.activitiesByStep[stepId] || []).length,
        include: (this.includeByStep[stepId] || []).length,
      };
    },
    selectStep(stepId) {
      this.selectedStepId = stepId;
      this.formData.step_id = stepId;
    },
    onChange(key, value) {
      this.formData[key] = value;
    },
    async onSubmit() {
      if (!this.formData.step_id) return;
      this.loading = true;
      await this.data?.onSubmit(this.formData);
      this.loading = false;
    },
    async loadData() {
      this.isFetching = true;
      const excludeStepIds = this.data?.excludeStepIds || [];
      const res = await loadStepLibraryPreviewData(excludeStepIds);
      this.isFetching = false;
      if (!res) return;

      this.steps = res.steps;
      this.conditionsByStep = res.conditionsByStep;
      this.activitiesByStep = res.activitiesByStep;
      this.includeByStep = res.includeByStep;

      if (this.selectedStepId && !this.steps.some((s) => s.step_id === this.selectedStepId)) {
        this.selectedStepId = null;
        this.formData.step_id = "";
      }
      if (!this.selectedStepId && this.steps.length === 1) {
        this.selectStep(this.steps[0].step_id);
      }
    },
  },
  mounted() {
    this.loadData();
  },
};
</script>

<style scoped lang="scss">
.form {
  // ширина — общая (ms-form в theme.scss)
  display: flex;
  flex-direction: column;
  gap: 12px;

  &__loading {
    display: flex;
    justify-content: center;
    padding: 40px 0;
  }

  &__layout {
    display: grid;
    grid-template-columns: 220px 1fr;
    gap: 12px;
    min-height: 320px;

    @media (max-width: 700px) {
      grid-template-columns: 1fr;
      min-height: auto;
    }
  }

  &__list {
    display: flex;
    flex-direction: column;
    min-height: 0;
  }

  &__search {
    margin-bottom: 8px;
  }

  &__list-scroll {
    flex: 1;
    overflow-y: auto;
    border: 1px solid var(--ms-border);
    border-radius: var(--ms-radius-sm);
    max-height: 300px;
  }

  &__step {
    display: grid;
    grid-template-columns: 28px 1fr auto;
    gap: 6px;
    align-items: start;
    width: 100%;
    padding: 8px 10px;
    border: none;
    border-bottom: 1px solid var(--ms-divider);
    background: var(--ms-surface);
    text-align: left;
    cursor: pointer;
    transition: background 0.12s;

    &:hover {
      background: var(--ms-row-hover);
    }

    &--active {
      background: var(--ms-primary-50);
      box-shadow: inset 3px 0 0 var(--ms-primary);
    }

    &:last-child {
      border-bottom: none;
    }
  }

  &__step-id {
    font-size: 10px;
    font-weight: 700;
    color: #64748b;
    padding-top: 2px;
  }

  &__step-name {
    font-size: 12px;
    color: var(--ms-text);
    line-height: 1.35;
  }

  &__step-badges {
    display: flex;
    flex-direction: column;
    gap: 2px;
  }

  &__badge {
    min-width: 16px;
    padding: 0 4px;
    border-radius: 10px;
    background: #e2e8f0;
    color: #475569;
    font-size: 9px;
    font-weight: 700;
    text-align: center;
    line-height: 1.5;
  }

  &__empty,
  &__preview-empty {
    font-size: 12px;
    color: var(--ms-muted);
    padding: 12px;
    line-height: 1.4;
  }

  &__preview {
    border: 1px solid var(--ms-border);
    border-radius: var(--ms-radius-sm);
    padding: 10px 12px;
    background: #fafbfc;
    overflow-y: auto;
    max-height: 300px;
  }

  &__preview-title {
    font-size: 13px;
    font-weight: 600;
    color: var(--ms-text);
    margin-bottom: 4px;
    line-height: 1.35;
  }

  &__preview-code {
    font-size: 11px;
    color: #64748b;
    margin-bottom: 10px;
  }

  &__active {
    margin-top: 2px;
    font-size: 13px;
  }
}
</style>
